import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

export type BadgeVariant = 'success' | 'secondary' | 'error' | 'default' | 'default-outline' | 'warning' | 'info';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  title?: string;
  semanticId?: string;
  children?: React.ReactNode;
}

const StyledBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$variant', '$size'].includes(String(prop)),
})<{ $variant: BadgeVariant; $size: BadgeSize }>`
  border-radius: var(--ui-radius-sm, 5px);
  font-weight: 600;
  font-size: ${rem(11)};
  line-height: ${rem(14)};
  padding: ${rem(5)} ${rem(14)};
  display: inline-block;
  font-family: var(--ui-font-sans);
  white-space: nowrap;

  ${({ $size }) =>
    $size === 'sm' &&
    css`
      font-size: ${rem(10)};
      line-height: ${rem(12)};
      padding: ${rem(3)} ${rem(10)};
    `}

  ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return css`
          background-color: var(--ui-status-success-muted);
          color: var(--ui-status-success);
        `;
      case 'error':
        return css`
          background-color: var(--ui-status-danger-muted);
          color: var(--ui-status-danger);
        `;
      case 'warning':
        return css`
          background-color: var(--ui-status-warning-muted);
          color: var(--ui-status-warning);
        `;
      case 'info':
        return css`
          background-color: var(--ui-interactive-light);
          color: var(--ui-interactive);
        `;
      case 'secondary':
        return css`
          background-color: var(--ui-interactive-light);
          color: var(--ui-interactive);
        `;
      case 'default-outline':
        return css`
          background-color: var(--ui-background, #ffffff);
          color: var(--ui-text-muted, #6b7280);
          border: 1px solid var(--ui-border-muted, #e5e7eb);
        `;
      case 'default':
      default:
        return css`
          background-color: var(--ui-background-muted);
          color: var(--ui-text-muted);
        `;
    }
  }}
`;

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(function Badge(
  { variant = 'success', size = 'md', title, semanticId, children, ...rest },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <StyledBadge
      ref={ref}
      $variant={variant}
      $size={size}
      data-variant={variant}
      {...semanticProps}
      {...rest}
    >
      {title ?? children}
    </StyledBadge>
  );
});

Badge.displayName = 'Badge';
